import { useEffect, useRef } from 'react';
import { useLocalSearchParams, router } from 'expo-router';
import { useUIStore } from '../store/uiStore';
import { useAuthStore } from '../store/authStore';
import { defensePlansApi } from '../api/defensePlans';
import { parsePanelDeepLinkParams } from './usePanelHistory';

/**
 * Consumes the desktop map's shareable panel deep links
 * (docs/plans/panel-system.md, "Deep links on load", Phase 4A):
 * `?plan=<id>` (`&assign=<featureId>`; a trailing `&people=1` is ignored) and `?post=<id>`.
 * Parsed once on mount (app/(tabs)/index.tsx calls this alongside
 * useMapFocus, whose own `focusStructureId` channel this composes with —
 * `&focusStructureId=` and `&post=` can both be present at once, each
 * consumed independently) and cleared with `router.setParams` right after,
 * the same pattern useMapFocus.ts already uses for `focusStructureId`, so
 * navigating back into this screen doesn't replay them. Native ignores this
 * entirely (defense plans and the desktop panels don't exist there) — but
 * this hook doesn't need to branch on Platform.OS itself: `plan`/`post`
 * params simply never arrive on a native deep link today.
 */
export function usePanelDeepLinks() {
  const params = useLocalSearchParams<{
    plan?: string;
    assign?: string;
    people?: string;
    post?: string;
  }>();
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    const parsed = parsePanelDeepLinkParams(params);
    if (!parsed.planId && !parsed.postId) return;
    consumedRef.current = true;

    if (parsed.postId) {
      useUIStore.getState().openDockedPost(parsed.postId);
    }

    if (parsed.planId) {
      const { planId, assignFeatureId } = parsed;
      void defensePlansApi.getPlan(planId).then((plan) => {
        if (!plan) return; // Unknown or no-longer-available plan id: ignore.
        const user = useAuthStore.getState().user;
        const isOwner = !!user && user.id === plan.ownerId;
        // The editor always shows the signed-in user's own plan (singular),
        // so pointing it at the id they own is just opening the editor —
        // same decision app/plan/[id].tsx would need if plans stop being
        // one-per-owner. Everyone else gets the read-only viewer, the same
        // channel a share link or a "plans in this area" card already uses.
        if (isOwner) {
          useUIStore.getState().openPlanPanel();
        } else {
          useUIStore.getState().requestMapPlanFocus(planId);
        }
        if (assignFeatureId) {
          useUIStore.getState().requestPlanAssignFocus(assignFeatureId);
        }
      });
    }

    router.setParams({ plan: undefined, assign: undefined, people: undefined, post: undefined });
    // Runs once (consumedRef above) — `params` is intentionally the only
    // dependency, since expo-router hands back a new object on every
    // navigation and this must re-check after `router.setParams` clears the
    // ones it just consumed.
  }, [params]);
}
